import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, signal, OnChanges } from '@angular/core';
import { AccessLevelType, AccessLevelTypeDescription, DocumentLegalValueCombination, DocumentLegalValueCombinationDescription } from '../../../../models/edocs.model';
import { FlowObjectDefinition } from '../../../../models/flow-object.model';
import { Templates } from '@formio/js';
import { EDocsService } from '../../../../services/edocs.service';
import { AuthService } from '../../../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../../shared/enums';
import { Utils } from '../../../../shared/utils';
import { MatDialog } from '@angular/material/dialog';
import { TemplateRef } from '@angular/core';
import { FORMBUILDER_SCHEMA_DEFAULT } from '../../../formio/formbuilder-schema-default';
import { FORMBUILDER_OPTIONS_INBOUND_API } from '../../../formio/formbuilder-options-inbound-api';
import { FormBuilderTemplates } from '../../../formio/formbuilder-templates';
import { FlowDefinition, FlowTarget } from '../../../../models/flow.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fod-start-inbound-api',
    templateUrl: './fod-start-inbound-api.component.html',
    styleUrl: './fod-start-inbound-api.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectDetailsStartInboundComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('wrapperRef') wrapperRef: ElementRef;
    @ViewChild('formSchemaRef') formSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('formSchemaEditorRef') formSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    AccessLevelType = AccessLevelType;
    AccessLevelTypeDescription = AccessLevelTypeDescription;
    DocumentLegalValueCombination = DocumentLegalValueCombination;
    DocumentLegalValueCombinationDescription = DocumentLegalValueCombinationDescription;
    // #endregion

    // #region [props]
    options: any = FORMBUILDER_OPTIONS_INBOUND_API;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $formSchema = signal(FORMBUILDER_SCHEMA_DEFAULT as any);
    $formSchemaFlat = signal('');
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        private authService: AuthService,
        private edocsService: EDocsService
    ) { }

    async ngOnInit() {
        // #region [injeção dos métodos para se obter as opções de dropdowns do E-Docs em componentes do tipo "PdfUpload"/"PdfUploadMultiple" no FormBuilder]
        let _this = this;

        let pdfUploadComponents = this.options.editForm.pdfUpload.find(x => x.key == 'display').components as any[];
        pdfUploadComponents = pdfUploadComponents.concat(this.options.editForm.pdfUploadMultiple.find(x => x.key == 'display').components as any[]);

        pdfUploadComponents.filter(x => x.key == 'documentLegalValueCombination').forEach(x => {
            x.data = {
                custom(context) {
                    let documentLegalValueCombinations = [];

                    for (let item in DocumentLegalValueCombination) {
                        if (!Number.isNaN(Number.parseInt(item))) {
                            documentLegalValueCombinations.push({
                                value: +item,
                                label: DocumentLegalValueCombinationDescription.get(+item)
                            });
                        }
                    }

                    return documentLegalValueCombinations;
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'accessLevel').forEach(x => {
            x.data = {
                custom(context) {
                    let accessLevels = [];

                    for (let item in AccessLevelType) {
                        if (!Number.isNaN(Number.parseInt(item)) && +item != AccessLevelType.Classified) {
                            accessLevels.push({
                                value: +item,
                                label: AccessLevelTypeDescription.get(+item)
                            });
                        }
                    }

                    return accessLevels;
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'activePlans').forEach(x => {
            x.customConditional = context => {
                return context.data.documentLegalValueCombination.value == DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoEletronicamente;
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        const patriarchId = _this._flowDefinition.organizationId;
                        const response = await _this.edocsService.getPlanosAtivos(patriarchId);

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        let activePlans = response.data.sort((a, b) => {
                            let first = a.codigo || a.nome || '';
                            let second = b.codigo || b.nome || '';
                            return first.localeCompare(second);
                        });

                        return activePlans.map(x => ({
                            value: x.id,
                            label: `${x.codigo} - ${x.nome.toUpperCase()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'documentClass').forEach(x => {
            x.customConditional = context => {
                return context.data.documentLegalValueCombination.value == DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoEletronicamente
                    && JSON.stringify(context.data.activePlans) != JSON.stringify({});
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        const response = await _this.edocsService.getClassesAtivas(context.data.activePlans.value);

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        // filtrando apenas as classes e não seus agrupadores
                        let documentClasses = response.data.filter(x =>
                            // XX.XX.XX.XX
                            x.codigo.split('.').length == 4
                            // XX.XXXX
                            || x.codigo.split('.').slice(-1)[0].length == 4
                        ).sort((a, b) => `${a.codigo.trim()} - ${a.nome.trim()}`.localeCompare(`${b.codigo.trim()} - ${b.nome.trim()}`));

                        return documentClasses.map(x => ({
                            value: x.id,
                            label: `${x.codigo.trim()} - ${x.nome.trim()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'legalReasoning').forEach(x => {
            x.customConditional = context => {
                return context.data.accessLevel.value == AccessLevelType.Confidential;
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        let response = null;
                        if (_this._flowDefinition.targetId == FlowTarget.Citizen) {
                            response = await _this.edocsService.getFundamentosLegaisCidadao();
                        } else {
                            const patriarchId = _this._flowDefinition.organizationId;
                            response = await _this.edocsService.getFundamentosLegaisPatriarca(patriarchId);
                        }

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        let legalReasonings = response.data.sort((a, b) => a.ordenacao - b.ordenacao).filter(x => x.nivelAcesso.toUpperCase() == "SIGILOSO");

                        return legalReasonings.map(x => ({
                            value: x.id,
                            label: `${x.nivelAcesso.trim()}: ${x.nome.trim()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });
        // #endregion

        const builderComponentForm = Templates.current.builderComponent.form;
        Templates.current.builderComponent.form = ctx => FormBuilderTemplates.builderComponentFormTemplate(builderComponentForm, ctx);

        const builderEditFormForm = Templates.current.builderEditForm.form;
        Templates.current.builderEditForm.form = ctx => FormBuilderTemplates.builderEditFormTemplate(builderEditFormForm, ctx);

        const datagridForm = Templates.current.datagrid.form;
        Templates.current.datagrid.form = ctx => FormBuilderTemplates.datagridFormTemplate(datagridForm, ctx);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    onTabChange() {
        if (Utils.isNotNullOrEmpty(this.$model()?.formSchema)) {
            this.$formSchema.set(JSON.parse(this.$model().formSchema));
        }
    }

    formBuilderChange(event) {
        if (event.form?.components.length > 0) {
            event.form.components.forEach(x => {
                if (this.$formSchema().components.length > 0) {
                    Utils.updSignal(this.$formSchema, xx => {
                        xx.components.filter(y => y.key == x.key).forEach(z => {
                            Object.assign(z, x);
                        });
                        return xx;
                    });
                } else {
                    Utils.setSignal(this.$formSchema, event.form);
                }
            });
        }

        Utils.updSignal(this.$model, x => {
            x.formSchema = JSON.stringify(this.$formSchema());
            return x;
        });
    }

    showFormSchema() {
        this.$formSchemaFlat.set(JSON.stringify(this.$formSchema(), null, 4));

        let dialog = this.dialog.open(this.formSchemaRef, {
            minWidth: '800px'
        });

        dialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.formSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.formSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.formSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        dialog.afterClosed().subscribe(() => {
            this.$formSchema.set(JSON.parse(this.formSchemaEditorRef.nativeElement.value));
            Utils.updSignal(this.$formSchema, x => {
                x.components = x.components.filter(x => ['pdfUpload', 'pdfUploadMultiple', 'button'].includes(x.type));
                return x;
            });
            Utils.updSignal(this.$model, x => {
                x.formSchema = JSON.stringify(this.$formSchema());
                return x;
            });
        });
    }

    onSubmit() {
        if (this.$isReadOnlyMode()) return false;

        Utils.updSignal(this.$model, x => {
            x.formSchema = JSON.stringify(this.$formSchema());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion
}
